import { useState, useRef, useCallback, useEffect } from "react";
import { wsClient } from "@/lib/websocket";

type CallState = "idle" | "calling" | "ringing" | "connected" | "ended";

interface UseWebRTCReturn {
  callState: CallState;
  isMuted: boolean;
  callDuration: number;
  startCall: (conversationId?: string) => Promise<void>;
  answerCall: (offer: RTCSessionDescriptionInit, conversationId?: string) => Promise<void>;
  hangUp: () => void;
  toggleMute: () => void;
}

// Fetch TURN credentials from server or use defaults
async function getIceServers(): Promise<RTCIceServer[]> {
  const defaultServers: RTCIceServer[] = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ];

  try {
    const res = await fetch("/api/config/turn-servers", { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      if (data.iceServers && data.iceServers.length > 0) {
        return [...defaultServers, ...data.iceServers];
      }
    }
  } catch {}

  // Fallback: STUN only (works on same network, not across NAT)
  return defaultServers;
}

export function useWebRTC(): UseWebRTCReturn {
  const [callState, setCallState] = useState<CallState>("idle");
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const callStateRef = useRef<CallState>("idle");
  const activeConvIdRef = useRef<string | undefined>();
  const pendingIceCandidates = useRef<RTCIceCandidateInit[]>([]);
  const iceServersRef = useRef<RTCIceServer[]>([{ urls: "stun:stun.l.google.com:19302" }]);

  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Fetch TURN config on mount
  useEffect(() => {
    getIceServers().then(servers => {
      iceServersRef.current = servers;
      console.log("[WebRTC] ICE servers configured:", servers.length, "servers", servers.some(s => JSON.stringify(s.urls).includes("turn")) ? "(with TURN)" : "(STUN only)");
    });
  }, []);

  useEffect(() => {
    return () => { cleanUp(); };
  }, []);

  // Register WebSocket listeners ONCE
  useEffect(() => {
    const unsubs = [
      // Note: call_incoming is handled by IncomingCallOverlay for admins.
      // Customers never receive call_incoming (server sends to admins only).
      // No handler needed here.
      wsClient.on("call_answered", async (data) => {
        console.log("[WebRTC] call_answered received, have PC:", !!pcRef.current, "have SDP:", !!data.sdp, "current state:", callStateRef.current);
        if (pcRef.current && data.sdp) {
          try {
            const sdp = data.sdp;
            // Ensure the SDP has the required type field
            const desc = new RTCSessionDescription({
              type: sdp.type || "answer",
              sdp: sdp.sdp,
            });
            await pcRef.current.setRemoteDescription(desc);
            console.log("[WebRTC] Remote description set (answer), flushing", pendingIceCandidates.current.length, "queued ICE candidates");
            for (const candidate of pendingIceCandidates.current) {
              try { await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch (err) {
                console.error("[WebRTC] Error adding queued ICE candidate:", err);
              }
            }
            pendingIceCandidates.current = [];
            setCallState("connected");
            startTimer();
          } catch (err) {
            console.error("[WebRTC] Error setting remote description:", err);
          }
        } else {
          console.error("[WebRTC] call_answered but missing PC or SDP — call may fail");
        }
      }),
      wsClient.on("rtc_ice", async (data) => {
        if (data.candidate) {
          if (!pcRef.current || !pcRef.current.remoteDescription) {
            pendingIceCandidates.current.push(data.candidate);
            return;
          }
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch (err) {
            console.error("[WebRTC] Error adding ICE candidate:", err);
          }
        }
      }),
      wsClient.on("call_ended", () => {
        cleanUp();
        setCallState("ended");
        setTimeout(() => setCallState("idle"), 2000);
      }),
    ];
    return () => { unsubs.forEach((u) => u()); };
  }, []);

  function createPeerConnection() {
    const config: RTCConfiguration = {
      iceServers: iceServersRef.current,
      iceCandidatePoolSize: 10,
    };
    console.log("[WebRTC] Creating PeerConnection with", config.iceServers?.length, "ICE servers");
    const pc = new RTCPeerConnection(config);

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        console.log("[WebRTC] Local ICE candidate:", e.candidate.type, e.candidate.protocol, e.candidate.address);
        wsClient.send({ type: "rtc_ice", candidate: e.candidate.toJSON(), conversationId: activeConvIdRef.current });
      } else {
        console.log("[WebRTC] ICE gathering complete");
      }
    };

    pc.ontrack = (e) => {
      console.log("[WebRTC] Remote track received:", e.track.kind, "enabled:", e.track.enabled, "muted:", e.track.muted, "streams:", e.streams.length);

      // Use the stream from the event, or create one from the track if no stream is associated
      const stream = e.streams[0] || new MediaStream([e.track]);

      if (remoteAudioRef.current) {
        remoteAudioRef.current.pause();
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current.remove();
      }
      const audio = document.createElement("audio");
      audio.autoplay = true;
      (audio as any).playsInline = true;
      audio.setAttribute("playsinline", "");
      audio.volume = 1.0;
      audio.style.display = "none";
      document.body.appendChild(audio);
      audio.srcObject = stream;

      // Monitor track state
      e.track.onunmute = () => {
        console.log("[WebRTC] Remote track unmuted");
        // Re-trigger play on unmute in case autoplay was blocked
        audio.play().catch(() => {});
      };
      e.track.onmute = () => console.log("[WebRTC] Remote track muted");
      e.track.onended = () => console.log("[WebRTC] Remote track ended");

      audio.play().then(() => console.log("[WebRTC] Audio playing, volume:", audio.volume, "paused:", audio.paused)).catch(err => {
        console.error("[WebRTC] Audio play failed:", err);
        // Register a click handler to resume playback (autoplay policy)
        const retry = () => { audio.play().catch(() => {}); document.removeEventListener("click", retry); };
        document.addEventListener("click", retry);
      });
      remoteAudioRef.current = audio;
    };

    pc.onconnectionstatechange = () => {
      console.log("[WebRTC] Connection:", pc.connectionState);
      if (pc.connectionState === "connected") {
        console.log("[WebRTC] Call fully connected — audio should be flowing");
      }
      if (pc.connectionState === "failed") {
        console.error("[WebRTC] Connection FAILED — attempting ICE restart");
        // Try ICE restart before giving up
        try {
          pc.restartIce();
        } catch {
          hangUp();
        }
      }
      if (pc.connectionState === "disconnected") {
        // Give it 5 seconds to recover before hanging up
        setTimeout(() => {
          if (pcRef.current?.connectionState === "disconnected") hangUp();
        }, 5000);
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("[WebRTC] ICE state:", pc.iceConnectionState);
      if (pc.iceConnectionState === "failed") {
        console.error("[WebRTC] ICE FAILED — restarting ICE");
        pc.restartIce();
      }
    };

    pc.onicegatheringstatechange = () => {
      console.log("[WebRTC] ICE gathering state:", pc.iceGatheringState);
    };

    pcRef.current = pc;
    return pc;
  }

  const startCall = useCallback(async (conversationId?: string) => {
    // Prevent starting a call if one is already in progress
    if (callStateRef.current !== "idle") {
      console.warn("[WebRTC] startCall ignored — already in state:", callStateRef.current);
      return;
    }
    activeConvIdRef.current = conversationId;
    console.log("[WebRTC] Starting call for conversation:", conversationId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPeerConnection();
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
        console.log("[WebRTC] Added local track:", track.kind, "enabled:", track.enabled);
      });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsClient.send({ type: "rtc_offer", sdp: pc.localDescription?.toJSON(), conversationId });
      setCallState("calling");
    } catch (err) {
      console.error("[WebRTC] startCall failed:", err);
      cleanUp();
      setCallState("idle");
    }
  }, []);

  const answerCall = useCallback(async (offer: RTCSessionDescriptionInit, conversationId?: string) => {
    // Prevent answering if already in a call
    if (callStateRef.current !== "idle" && callStateRef.current !== "ringing") {
      console.warn("[WebRTC] answerCall ignored — already in state:", callStateRef.current);
      return;
    }
    activeConvIdRef.current = conversationId;
    console.log("[WebRTC] Answering call for conversation:", conversationId, "offer type:", offer?.type);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPeerConnection();
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
        console.log("[WebRTC] Added local track:", track.kind, "enabled:", track.enabled);
      });

      // Ensure the offer SDP has the required type field
      const offerDesc = new RTCSessionDescription({
        type: offer.type || "offer",
        sdp: offer.sdp,
      });
      await pc.setRemoteDescription(offerDesc);
      console.log("[WebRTC] Remote description set (offer)");

      // Flush any ICE candidates that were queued before remote description was set
      if (pendingIceCandidates.current.length > 0) {
        console.log("[WebRTC] Flushing", pendingIceCandidates.current.length, "queued ICE candidates after setRemoteDescription (answer side)");
        for (const candidate of pendingIceCandidates.current) {
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (err) {
            console.error("[WebRTC] Error adding queued ICE candidate:", err);
          }
        }
        pendingIceCandidates.current = [];
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log("[WebRTC] Sending answer SDP to conversation:", conversationId);
      wsClient.send({ type: "rtc_answer", sdp: pc.localDescription?.toJSON(), conversationId });
      setCallState("connected");
      startTimer();
    } catch (err) {
      console.error("[WebRTC] answerCall failed:", err);
      cleanUp();
      setCallState("idle");
    }
  }, []);

  const hangUp = useCallback(() => {
    wsClient.send({ type: "rtc_hangup", conversationId: activeConvIdRef.current });
    cleanUp();
    setCallState("idle");
  }, []);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) { track.enabled = !track.enabled; setIsMuted(!track.enabled); }
    }
  }, []);

  function startTimer() {
    setCallDuration(0);
    timerRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
  }

  function cleanUp() {
    clearInterval(timerRef.current);
    setCallDuration(0);
    pcRef.current?.close();
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current.remove();
      remoteAudioRef.current = null;
    }
    pendingIceCandidates.current = [];
  }

  return { callState, isMuted, callDuration, startCall, answerCall, hangUp, toggleMute };
}
